import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { LOGO_URL } from '../mock/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Phone, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Login = () => {
  const { requestOtp, verifyOtp } = useAuth();
  const settings = useSettings();
  const logoSrc = settings?.general?.logo_url || LOGO_URL;
  const siteName = settings?.general?.site_name || 'TrainPanda';
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const sendOtp = async () => {
    if (mobile.length !== 10) { toast({ title: 'Enter valid 10-digit mobile' }); return; }
    setLoading(true);
    try {
      await requestOtp(mobile);
      toast({ title: 'OTP sent!', description: `Verification code sent to +91 ${mobile}` });
      setStep(2);
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (otp.length !== 6) { toast({ title: 'Enter 6-digit OTP' }); return; }
    setLoading(true);
    try {
      await verifyOtp(mobile, otp);
      toast({ title: 'Welcome to TrainPanda!' });
      nav('/');
    } catch (e) { toast({ title: 'Wrong OTP', description: e.response?.data?.detail || 'Invalid OTP. Please check SMS.' }); }
    finally { setLoading(false); }
  };

  return (
    <div className="py-8 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl card-shadow p-8 border border-gray-100">
        <div className="flex justify-center mb-5">
          <img src={logoSrc} alt={siteName} className="h-14" />
        </div>
        {step === 1 && (
          <div>
            <h1 className="text-2xl font-bold text-[#0B2E5C] text-center">Login / Sign up</h1>
            <p className="text-sm text-gray-500 text-center mt-1">Enter your mobile number to continue</p>
            <div className="mt-6">
              <label className="text-xs font-semibold text-gray-500 uppercase">Mobile Number</label>
              <div className="mt-1 flex items-center gap-2 border-2 border-gray-200 rounded-lg focus-within:border-[#0B2E5C] px-3">
                <Phone size={18} className="text-gray-400" />
                <span className="text-sm text-gray-600">+91</span>
                <Input value={mobile} onChange={e=>setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit mobile" className="border-0 focus-visible:ring-0 h-12 text-base" />
              </div>
              <Button onClick={sendOtp} className="w-full mt-5 bg-tp-orange hover:bg-[#E07A0F] text-white font-bold h-12" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Send OTP'}
              </Button>
              <p className="text-[11px] text-gray-400 text-center mt-4">By continuing you agree to our Terms &amp; Privacy Policy</p>
            </div>
          </div>
        )}
        {step === 2 && (
          <div>
            <button onClick={()=>setStep(1)} className="text-sm text-gray-500 flex items-center gap-1 hover:text-tp-orange"><ArrowLeft size={14} /> Change number</button>
            <h1 className="text-2xl font-bold text-[#0B2E5C] text-center mt-3">Verify OTP</h1>
            <p className="text-sm text-gray-500 text-center mt-1">Sent to +91 {mobile}. Please enter the 6-digit code received via SMS.</p>
            <div className="mt-6">
              <label className="text-xs font-semibold text-gray-500 uppercase">6-digit OTP</label>
              <Input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="Enter OTP" className="h-14 text-center text-xl tracking-[0.5em] font-bold mt-1 border-2" />
              <Button onClick={verify} className="w-full mt-5 bg-tp-orange hover:bg-[#E07A0F] text-white font-bold h-12" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Verify & Login'}
              </Button>
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 justify-center">
                <ShieldCheck size={14} className="text-green-500" /> Your data is secure &amp; encrypted
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
